import { AlertCircle, CheckSquare } from "lucide-react";
import type React from "react";
import ReactMarkdown from "react-markdown";
import { useSettings } from "../contexts/SettingsContext";


interface AgenticChecklistProps {
  content: string;
}

const AgenticChecklist: React.FC<AgenticChecklistProps> = ({ content }) => {
  const {  debugMode } = useSettings();
  const parseContent = (text: string) => {
    // Check if content starts with "Great" and contains parameter assignments
  const capturedMatch = text.match(
    /^(Great\s+[^\n]+captured:)\s*([\s\S]+?)(\n|$)/
  );

    if (capturedMatch) {
      const intro = capturedMatch[1];
      const paramsText = capturedMatch[2];
      const restOfContent = text.substring(capturedMatch[0].length);

      // Parse parameters (looking for pattern: key=value)
      const params = paramsText
        .split(",")
        .map((param) => {
          const trimmed = param.trim();
          const [key, ...valueParts] = trimmed.split("=");
          const value = valueParts.join("=");
          return { key: key?.trim(), value: value?.trim() };
        })
        .filter((p) => p.key && p.value);

      if (params.length > 0) {
        return {
          type: "captured" as const,
          intro,
          params,
          rest: restOfContent,
        };
      }
    }

    return { type: "standard" as const, content: text };
  };

  const parsed = parseContent(content);

  return (
    <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 rounded-lg shadow-sm my-2 overflow-hidden">
      <div className="bg-gradient-to-r from-yellow-600 to-orange-600 px-5 py-3 flex items-center space-x-3">
        <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
          <CheckSquare className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-white font-semibold text-base">
            Missing Information
          </h3>
          <p className="text-purple-100 text-xs">
            Please provide the following details
          </p>
        </div>
      </div>

      <div className="p-6 text-gray-600 text-base">
        {parsed.type === "captured" ? (
          <div>
            <p className="mb-3 text-base">{parsed.intro}</p>
            <ul className="space-y-1.5 mb-3">
              {parsed.params.map((param, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-green-400 mr-2">✓</span>
                  <span>
                    <strong className="font-semibold text-gray-600 text-base">
                      {param.key}
                    </strong>
                    <span className="text-gray-600">: </span>
                    <span className="text-gray-600">{param.value}</span>
                  </span>
                </li>
              ))}
            </ul>
            {parsed.rest && (
              <div className="markdown-content">
                <ReactMarkdown
                  components={{
                    strong: ({ children }) => (
                      <strong className="font-bold text-gray-600 text-base">
                        {children}
                      </strong>
                    ),
                    p: ({ children }) => (
                      <p className="whitespace-pre-wrap mb-2 last:mb-0 text-base">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside space-y-1 text-base">
                        {children}
                      </ul>
                    ),
                    li: ({ children }) => <li className="ml-2">{children}</li>,
                  }}
                >
                  {parsed.rest}
                </ReactMarkdown>
              </div>
            )}
          </div>
        ) : (
          <div className="markdown-content">
            {(() => {
              // split at the visible marker
              const marker = "• Missing Required Fields:";
              const parts = parsed.content.split(marker);
              const beforePart = parts[0] ?? "";
              const afterPart = parts[1]
                ? `${marker}${parts[1]}`
                : parsed.content;

              // Build final raw markdown:
              // - include 'before' only when debugMode true
              // - always include everything from the marker onward
              const raw = debugMode
                ? `${beforePart.trim()}\n\n${afterPart.trim()}`
                : afterPart.trim();

              // remove "Say cancel to stop." (case-insensitive)
              const noCancel = raw
                .replace(/Say\s+['"]?cancel['"]?\s+to\s+stop\.?/gi, "")
                .trim();

              // Transform headings: bold the headings and add spacing for Checklist
              const transformed = noCancel
                .replace(
                  /•\s*Missing Required Fields\s*:/i,
                  "\n**• Missing Required Fields:**\n"
                )
                .replace(
                  /Checklist for case(?:\s+N\/A)?\s*:/i,
                  "\n\n**Checklist for case:**\n\n"
                );

              return (
                <ReactMarkdown
                  // valid react-markdown component overrides
                  components={{
                    strong: ({ children }) => (
                      <strong className="font-bold text-gray-600 text-base">
                        {children}
                      </strong>
                    ),
                    p: ({ children }) => (
                      <p className="whitespace-pre-wrap mb-2 last:mb-0 text-gray-600 text-base">
                        {children}
                      </p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside space-y-1 text-gray-600 text-base">
                        {children}
                      </ul>
                    ),
                    li: ({ children }) => (
                      <li className="ml-2 text-gray-600 base">{children}</li>
                    ),
                  }}
                >
                  {transformed}
                </ReactMarkdown>
              );
            })()}
          </div>
        )}
      </div>

      <div className="px-6 py-4 flex items-start space-x-3 border-t border-yellow-200 bg-yellow-100">
        <AlertCircle className="w-5 h-5 text-gray-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm text-gray-600 leading-relaxed">
            <span className="font-medium text-gray-600">Tip:</span> Provide the
            missing fields above, or type{" "}
            <code className="text-white bg-yellow-700 px-2 py-0.5 rounded font-semibold text-xs">
              cancel
            </code>{" "}
            to exit this request.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AgenticChecklist;
